import { mapCurrencyFromDTO } from '@/modules/common/mappers/currency.mapper';

import {
  OpsiumUserStatusDTO,
  PatchEntityEffectActionDTO,
  PatchEntityEffectDTO,
  TeamMemberChangeConflictResponseDTO,
  TeamMemberChangeDataDTO,
} from '../types/dto/team-member-conflict.dto';
import { MembershipType } from '../types/membership-type';
import { MemberStatus } from '../types/team-member';
import {
  ConflictEntityReference,
  PatchEffectAction,
  PatchEntityEffect,
  TeamMemberConflictResolutionData,
  TeamMemberConflictSnapshot,
  TeamMemberVersionedFields,
} from '../types/team-member-conflicts';

/**
 * Maps OpsiumUserStatus from DTO to domain MemberStatus
 * Simplified mapping for conflict snapshots (only ACTIVE/ARCHIVED)
 */
const mapMemberStatusFromConflictDTO = (status: OpsiumUserStatusDTO): MemberStatus => {
  switch (status) {
    case OpsiumUserStatusDTO.ARCHIVED:
      return MemberStatus.Archived;
    case OpsiumUserStatusDTO.ACTIVE:
    default:
      return MemberStatus.Active;
  }
};

/**
 * Maps patch effect action from DTO to domain enum
 */
export const mapPatchEffectActionFromDTO = (dto: PatchEntityEffectActionDTO): PatchEffectAction => {
  switch (dto) {
    case PatchEntityEffectActionDTO.DELETE:
      return PatchEffectAction.Delete;
    case PatchEntityEffectActionDTO.CUT:
      return PatchEffectAction.Cut;
    default:
      return PatchEffectAction.Cut;
  }
};

/**
 * Maps patch entity effect from DTO to domain model
 */
export const mapPatchEntityEffectFromDTO = (dto: PatchEntityEffectDTO): PatchEntityEffect => {
  return {
    action: mapPatchEffectActionFromDTO(dto.action),
    cutEffectiveStartDate: dto.cutEffectiveStartDate,
    cutEffectiveEndDate: dto.cutEffectiveEndDate,
  };
};

/**
 * Helper to map entity reference from DTO
 */
const mapEntityReference = (
  dto: { id: number; name: string } | null
): ConflictEntityReference | null => {
  if (!dto) return null;
  return { id: dto.id, name: dto.name };
};

/**
 * Maps team member versioned fields from conflict DTO to domain model
 */
export const mapTeamMemberVersionedFieldsFromDTO = (
  dto: TeamMemberChangeDataDTO
): TeamMemberVersionedFields => {
  return {
    // Info section
    status: mapMemberStatusFromConflictDTO(dto.status),
    department: mapEntityReference(dto.department),
    role: mapEntityReference(dto.employeeRoleResponse),
    seniority: mapEntityReference(dto.seniority),
    country: dto.country,
    city: dto.city,
    manager: mapEntityReference(dto.manager),
    membershipType: dto.membershipType as MembershipType | null,
    isManager: dto.isManager,

    // Capacity section
    isOnDemand: dto.onDemand,
    totalWorkingMinutes: dto.totalWorkingMinutes,
    billableCapacity: dto.billableCapacity,

    // Billing section
    isHourlyRated: dto.isHourlyRated,
    currency: mapCurrencyFromDTO(dto.currency),
    internalRate: dto.internalRate,
    externalRate: dto.externalRate,
  };
};

/**
 * Maps a single conflict response to domain snapshot
 */
export const mapTeamMemberConflictSnapshotFromDTO = (
  dto: TeamMemberChangeConflictResponseDTO
): TeamMemberConflictSnapshot => {
  return {
    id: dto.change.id,
    snapshot: mapTeamMemberVersionedFieldsFromDTO(dto.change),
    patchEffect: mapPatchEntityEffectFromDTO(dto.patchEffect),
    effectiveStartDate: dto.change.effectiveStartDate,
    effectiveEndDate: dto.change.effectiveEndDate,
    description: dto.description,
  };
};

/**
 * Maps team member conflicts array from DTO to domain model
 */
export const mapTeamMemberConflictsFromDTO = (
  dtos: TeamMemberChangeConflictResponseDTO[]
): TeamMemberConflictResolutionData => {
  const items = dtos.map(mapTeamMemberConflictSnapshotFromDTO);

  return {
    items,
    hasConflicts: items.length > 0,
  };
};
